import Grid2 from '@thirdparty-framework/mui/material/Grid';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  FormHelperText,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import Label from '../Forms/Label';
import { useEntityPermission } from '../../providers/EntityPermissionProvider';
import { useLocalization } from '../../providers/LocalizationProvider';

/*
 * typeof field?.column === 'string' is used to determine if the column is a string or a number.
 * If it's a string, we don't set gridRow, otherwise we set it to field?.row
 * This is useful for cases where you need fullwidth field and define column as "1/-1".
 */

/*
input 
{
 name:
 control: (optional)
 label:{localeId, defaultMessage},
 column:
  row: 
}
*/

const SHOW_DIRTY_FIELDS = true;

function VerticalPlacement({
  formatMessage,
  error,
  hideError,
  ToolTipComponent,
  style,
  readOnly,
  dirtyFields,
  theme,
  helperText,
  field,
  children,
}) {
  return (
    <Grid2 xs="grow" key={field?.name} style={style}>
      <Label
        htmlFor={field?.name.replace('.', '-')}
        style={{ display: 'block', marginBottom: '5px' }}
        error={!!error}
        focused={!!error}
        required={field?.required && !readOnly}>
        {formatMessage({
          id: field?.label?.localeId,
          defaultMessage: field?.label?.defaultMessage,
        })}
        {field?.labelTooltip?.localeId && (
          <Tooltip
            arrow
            placement="top"
            title={formatMessage({
              id: field.labelTooltip.localeId,
              defaultMessage: field.labelTooltip.defaultMessage,
            })}>
            {/* Focusable with an accessible name so keyboard and screen
                reader users can reach the tooltip content too. */}
            <IconButton
              size="small"
              aria-label={formatMessage({
                id: field.labelTooltip.localeId,
                defaultMessage: field.labelTooltip.defaultMessage,
              })}
              sx={{ ml: 0.25, p: 0.25, verticalAlign: 'text-bottom' }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active' }} />
            </IconButton>
          </Tooltip>
        )}
        {ToolTipComponent && <ToolTipComponent />}
        {SHOW_DIRTY_FIELDS && dirtyFields?.[field?.name] && (
          <Tooltip placement="top" arrow title="Not Saved">
            <span
              style={{
                float: 'right',
                marginRight: '10px',
                display: 'inline-block',
                color: theme.palette.warning.dark,
              }}>
              <FiberManualRecordIcon sx={{ fontSize: '10px' }} />
            </span>
          </Tooltip>
        )}
      </Label>{' '}
      <Box
        style={{
          ...(field?.control === 'subsection'
            ? {
                border: `1px solid ${error ? theme.palette.error.main : '#ccc'}`,
                borderRadius: '10px',
                padding: '8px',
              }
            : {}),
        }}>
        {children}
      </Box>
      {(error || helperText) &&
      field?.control === 'subsection' &&
      !hideError ? (
        <FormHelperText sx={{ typography: 'body2' }} error={Boolean(error)}>
          {error?.message || helperText}
        </FormHelperText>
      ) : null}
    </Grid2>
  );
}

// Do not change labelMaxWidth in here, adjust it per component
function HorizontalPlacement({
  formatMessage,
  error,
  hideError,
  ToolTipComponent,
  style,
  readOnly,
  dirtyFields,
  theme,
  helperText,
  field,
  children,
  labelMaxWidth = '200px',
}) {
  return (
    <Grid2 xs="grow" key={field?.name} style={style}>
      <Grid2 container>
        <Grid2 xs={3} md={3} sx={{ maxWidth: labelMaxWidth, marginTop: '5px' }}>
          <Label
            htmlFor={field?.name.replace('.', '-')}
            error={!!error}
            focused={!!error}
            required={field?.required && !readOnly}>
            {formatMessage({
              id: field?.label?.localeId,
              defaultMessage: field?.label?.defaultMessage,
            })}
            {ToolTipComponent && <ToolTipComponent />}
            {SHOW_DIRTY_FIELDS && dirtyFields?.[field?.name] && (
              <Tooltip placement="top" arrow title="Not Saved">
                <span
                  style={{
                    color: theme.palette.warning.dark,
                    marginRight: '20px',
                    float: 'right',
                  }}>
                  <FiberManualRecordIcon sx={{ fontSize: '10px' }} />
                </span>
              </Tooltip>
            )}
          </Label>
        </Grid2>
        <Grid2 xs={9} md={9}>
          <Box
            style={{
              ...(field?.control === 'subsection'
                ? {
                    border: `1px solid ${error ? theme.palette.error.main : '#ccc'}`,
                    borderRadius: '10px',
                    padding: '8px',
                  }
                : {}),
            }}>
            {children}
          </Box>
          {(error || helperText) &&
          field?.control === 'subsection' &&
          !hideError ? (
            <FormHelperText sx={{ typography: 'body2' }} error={Boolean(error)}>
              {error?.message || helperText}
            </FormHelperText>
          ) : null}
        </Grid2>
      </Grid2>
    </Grid2>
  );
}

function FieldWrapperVertical({
  field,
  errors = [],
  customComponents = [],
  helperText = null,
  children,
  horizontal = false,
  labelMaxWidth = '200px',
}) {
  const { formatMessage } = useLocalization();
  const theme = useTheme();
  const {
    formState: { dirtyFields, errors: formStateErrors },
  } = useFormContext();

  const { isReadOnly } = useEntityPermission();
  const readOnly = isReadOnly(field?.permission, '');

  if (!field?.label?.localeId) {
    return children;
  }

  let style = {};
  if (field?.column && !horizontal) {
    style = {
      gridRow: typeof field?.column === 'string' ? null : field?.row,
      gridColumn: field?.column,
    };
  }

  const ToolTipComponent = customComponents?.[field?.toolTipComponent] || null;

  let error = formStateErrors[field?.name];
  const { hideError = false } = field;

  if (field?.control === 'subsection' && field?.validateFieldGroup) {
    error = field?.validateFieldGroup.reduce((acc, f) => {
      if (errors?.[f]?.message) {
        const newMessages = [
          ...(acc?.message ? acc.message.split(',') : []),
          errors?.[f]?.message,
        ]
          .filter(Boolean)
          .join(', ');
        return {
          message: newMessages,
        };
      }
      return acc;
    }, null);
  }

  if (horizontal) {
    return (
      <HorizontalPlacement
        field={field}
        formatMessage={formatMessage}
        error={error}
        hideError={hideError}
        ToolTipComponent={ToolTipComponent}
        style={style}
        readOnly={readOnly}
        dirtyFields={dirtyFields}
        labelMaxWidth={labelMaxWidth}
        theme={theme}
        helperText={helperText}>
        {children}
      </HorizontalPlacement>
    );
  }

  return (
    <VerticalPlacement
      field={field}
      formatMessage={formatMessage}
      error={error}
      hideError={hideError}
      ToolTipComponent={ToolTipComponent}
      style={style}
      readOnly={readOnly}
      dirtyFields={dirtyFields}
      theme={theme}
      helperText={helperText}>
      {children}
    </VerticalPlacement>
  );
}

export default FieldWrapperVertical;
